import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import Card from "src/components/common/ImageCard";
import { GridColDef } from "@mui/x-data-grid";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import DataGrid from "src/components/common/DataGrid";
import { AutoComplete, Input } from "src/components/common/FormComponents";
import Checkbox from "src/components/common/FormComponents/Checkbox";
import MapComponent from "src/components/common/KML";
import { SuccessModal } from "src/components/common/modal";
import errorMessage from "src/constants/errorMessage";
import { formHeaders } from "src/constants/sampleData";
import { setClose } from "src/redux/slices/newProjectSetup";
import { useAppDispatch, useAppSelector } from "src/redux/store";
import { getStyles } from "src/styles/theme";
import {
  getAllProjectOptions,
  getProjectOptions,
  getProjectOptionsDetails,
  registerTreePlantation,
} from "src/utils/api/createProjectFlow";
import { getProjectById } from "src/utils/api/dashboard";
import { optionTypeFormatter } from "src/utils/helper";
import defaultStyles, { StylesClasses } from "./styles";
import { treePlantationTypes, Utilization } from "./treePlantTypes";
import ViewForm from "src/components/common/ViewForm";
import ViewImages from "src/components/common/ViewImage";

const formHeads = formHeaders["PlantationBlock"];

const LandParcelSelection = () => {
  const stateOption = useAppSelector((state) => state?.root.state.allStates);
  const styles = getStyles<StylesClasses>(defaultStyles);
  const [gridAreas, setGridAreas] = useState([]);
  const [landArea, setLandArea] = useState("");
  const [projectId, setProjectId] = useState("");

  const [tableRows, setTableRows] = useState([]);

  const [openModal, setOpenModal] = useState({
    open: false,
    heading: "",
    subheading: "",
    description: "",
    btnText: "",
    type: "",
  });
  //video
  const [kmlInfo, setKmlInfo] = useState<any>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<any>(null);
  const [showBlockedDialog, setShowBlockedDialog] = useState(false);

  // One-time block: submit is disabled until Monday June 8, 2026 at 15:00 IST (UTC+5:30)
  // Using explicit IST offset so the unblock time is the same absolute moment regardless of user's browser timezone
  const SUBMIT_UNBLOCK_TIME = new Date("2026-06-08T15:00:00+05:30");
  const isSubmitBlocked = new Date() < SUBMIT_UNBLOCK_TIME;

  const tableColumns: GridColDef[] = [
    {
      field: "id",
      headerName: "ID",
      width: 200,
      sortable: false,
      hide: true,
    },
    {
      field: "registrationNo",
      headerName: "Reg. No",
      width: 100,
      sortable: false,
    },
    // {
    //   field: "areaUnit",
    //   headerName: "Total Area",
    //   width: 200,
    //   sortable: false,
    // },
    {
      field: "availableArea",
      headerName: "Available Area",
      width: 200,
      sortable: false,
    },
    {
      field: "state",
      headerName: "State",
      width: 200,
      sortable: false,
    },
    {
      field: "district",
      headerName: "District",
      width: 200,
      sortable: false,
    },
  ];

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { control, getValues, setValue, formState, watch, trigger } =
    useForm<treePlantationTypes>({
      mode: "onChange",
    });

  const { refetch: fetchProject } = useQuery(
    "projectOptions",
    () =>
      getProjectOptions({
        projectType: "LAND_LEASED",
        selectedState: watch("stateName"),
        // selectedDistrict: watch("districtName"),
      }),
    {
      refetchOnMount: true,
      onSuccess: (data) => {
        setTableRows(
          data?.data?.rows.map((row) => ({
            data: row,
            id: row.id,
            registrationNo: row.registrationNo,
            areaUnit: `${(+row.landArea).toFixed(2)} ${row.areaUnit}`,
            availableArea: `${(+row.availableArea).toFixed(2)} ${row.areaUnit}`,
            state: row.address.stateDetail.name,
            district: row.address.districtDetail.name,
          }))
        );
      },
      enabled: false,
    }
  );
  const { refetch: fetchAllProject } = useQuery(
    "projectOptions",
    () =>
      getAllProjectOptions({
        projectType: "LAND_LEASED",
        selectedState: watch("stateName"),
        // selectedDistrict: watch("districtName"),
      }),
    {
      refetchOnMount: true,
      onSuccess: (data) => {
        setTableRows(
          data?.data?.rows.map((row) => ({
            data: row,
            id: row.id,
            registrationNo: row.registrationNo,
            areaUnit: `${(+row.landArea).toFixed(2)} ${row.areaUnit}`,
            availableArea: `${(+row.availableArea).toFixed(2)} ${row.areaUnit}`,
            state: row.address.stateDetail.name,
            district: row.address.districtDetail.name,
          }))
        );
      },
      enabled: false,
    }
  );

  const { data: projectOptionsDetails, refetch: fetchProjectDetails } =
    useQuery(
      ["projectOptionsDetails", projectId],
      () =>
        getProjectOptionsDetails({
          ID: projectId,
        }),
      {
        refetchOnMount: true,
        onSuccess: (res) => {
          setLandArea(
            `${
              (res?.data?.landSpecification?.totalArea &&
                res?.data?.landSpecification?.occupiedArea &&
                (
                  +res?.data?.landSpecification?.totalArea -
                  +res?.data?.landSpecification?.occupiedArea
                ).toFixed(2)) ??
              "-"
            }`
          );
        },
        enabled: !!projectId,
      }
    );

  // Fetch full project details by internal project id (same as getProjectById used elsewhere)
  const { data: projectFullDetails, isLoading: isProjectFullLoading } =
    useQuery(
      ["projectFullDetails", projectId],
      () => getProjectById(projectId),
      {
        enabled: !!projectId,
        onSuccess: (res) => {
          // debug
          // eslint-disable-next-line no-console
          console.log("getProjectById response:", res);
        },
      }
    );

  // prefer the full project details if available, otherwise fall back to projectOptionsDetails
  const projectData = projectFullDetails?.data ?? projectOptionsDetails?.data;

  const { isLoading: submitLoading, mutate } = useMutation(
    "tree-plantation-form",
    (data: {
      projectId: string;
      grids: {
        id: number;
        name: string;
      }[];
      totalArea: number;
      utilization?: Utilization;
      nonTradableCredit?: string;
      nonTransferableCredit?: string;
    }) => registerTreePlantation(data),
    {
      onSuccess: () => {
        setOpenModal({
          open: true,
          heading: "Congratulations!",
          subheading: "Eco-Restoration Blocks Selected Successfully!",
          description: "",
          btnText: "Done",
          type: "success",
        });
        //refetch();
      },
    }
  );

  const stateOptions = optionTypeFormatter(stateOption);

  const totalSelectedLand = () => {
    const selectedLandArea = getValues("selectLandArea");
    setValue("totalArea", selectedLandArea ? "" : landArea);
    trigger("totalArea");
  };

  const { isValid, errors } = formState;

  useEffect(() => {
    if (!!watch("stateName")) {
      fetchProject();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch("stateName")]);

  useEffect(() => {
    fetchAllProject();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!!watch("projectName")) {
      fetchProjectDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch("projectName")]);

  //video
  useEffect(() => {
    if (!projectId) return;

    getProjectOptionsDetails({ ID: projectId })
      .then((res) => {
        setKmlInfo(res.data);
      })
      .catch((err) => {
        console.error("Failed to fetch KML info", err);
      });
  }, [projectId]);

  const kmlDocument = kmlInfo?.documentDetail?.find(
    (doc) => doc.type === "LAND_KML_FILE"
  );
  const kmlFileUrl = kmlDocument?.documentUrl?.fileUrl;

  const droneVideoFromKmlInfo = kmlInfo?.documentDetail?.find(
    (doc) => doc.type === "DRONE_VIDEO"
  );

  const droneVideoUrl = droneVideoFromKmlInfo?.documentUrl?.fileUrl;

  //images
  const projectImages = kmlInfo?.images ?? [];
  const area = getValues("totalArea");
  const showButton = +landArea > 0;

  //radio button

  const utilizationOptions = [
    {
      id: "a",
      label:
        "meeting the compliance of the compensatory afforestation in cases related to diversion of forest land for non-forestry purposes as laid down under the Van (Sanrakshan Evam Samvardhan) Adhiniyam, 1980, and its rules and the guidelines framed thereunder;",
    },
    {
      id: "b",
      label:
        "meeting the requirements under Corporate Social Responsibility, as per the provisions of any law for the time being in force, based on cost incurred towards forest restoration activities;",
    },
    {
      id: "c",
      label:
        "meeting the requirement of plantation of trees to fulfill the obligations such as towards exploration/survey or for construction of infrastructure projects in compliance of the approval of the project being undertaken by the applicant person or entity as per the provisions of any law for the time being in force.",
    },
  ];
  const [selectedValue, setSelectedValue] = useState("b");

  const handleChange = (event) => {
    setSelectedValue(event.target.value);
  };

  //second radio button
  const UTILIZATION_2_VALUE = "second-utilization";
  const UTILIZATION_2_CONTENT =
    "The Green Credit generated in lieu of tree plantation under the said Rules may be used for reporting under environmental, social and governance leadership indicator as per the provisions of any law for the time being in force.";
  const [selectedValue2, setSelectedValue2] = useState(null);

  const handleToggleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, value: string) => {
      setSelectedValue2((prev) => (prev === value ? "" : value));
    },
    []
  );
  const canopyDensity = projectData?.landSpecification?.treeCanopyDensity;

  const handleSubmitForm = async () => {
    if (isSubmitBlocked) {
      setShowBlockedDialog(true);
      return;
    }
    const valid = await trigger();
    if (!valid) return;
    const primaryUtilization =
      utilizationOptions.find((o) => o.id === selectedValue) ?? null;
    const secondaryUtilization =
      selectedValue2 === UTILIZATION_2_VALUE
        ? { id: UTILIZATION_2_VALUE, label: UTILIZATION_2_CONTENT }
        : null;
    const landFree = getValues("nonTradableCredit");
    const landSuitable = getValues("nonTransferableCredit");

    const formData = {
      projectId,
      grids: gridAreas,
      totalArea: Number(getValues("totalArea")),
      utilization: {
        primary: primaryUtilization
          ? { id: primaryUtilization.id, label: primaryUtilization.label }
          : null,
        secondary: secondaryUtilization,
      },
      nonTradableCredit: landFree || "",
      nonTransferableCredit: landSuitable || "",
    };

    // Store form data and show confirmation dialog
    setPendingFormData(formData);
    setShowConfirmDialog(true);
  };

  const handleConfirmSubmit = () => {
    if (!pendingFormData) {
      console.error("Form data is not available");
      return;
    }
    setShowConfirmDialog(false);
    mutate(pendingFormData);
  };

  const handleCancelSubmit = () => {
    setShowConfirmDialog(false);
    setPendingFormData(null);
  };
  return (
    <>
      <h3>Select Eco-Restoration Block</h3>

      <Box {...styles("responsiveBox")}>
        <Box>
          <Box {...styles("formTitle")}>{formHeads.title}</Box>
          <Box {...styles("input")}>
            <AutoComplete
              options={stateOptions}
              onOptionSelect={(e) => {
                if (stateOption?.length && e) {
                  setValue("districtName", "");
                }
              }}
              customStyles={{
                label: defaultStyles.label,
                wrapper: defaultStyles.selectWrapper,
              }}
              name="stateName"
              // rules={{ required: errorMessage.required }}
              control={control}
              placeholder="State"
              label="State"
            />
          </Box>
          <Box
            sx={{
              fontSize: "18px",
              fontWeight: "500",
              mt: "30px",
            }}
          >
            Please select area for plantation
          </Box>
          <Box
            sx={{
              ".MuiDataGrid-cell:focus": {
                outline: "none",
              },
              "& .MuiDataGrid-row:hover": {
                cursor: "pointer",
              },
              mt: "20px",
            }}
          >
            <DataGrid
              rows={tableRows || []}
              columns={tableColumns}
              rowCount={tableRows.length}
              rowsPerPageOptions={[10]}
              paginationMode="client"
              hidePagination={true}
              pageSize={8}
              onCellClick={(data) => setProjectId(data.row?.id)}
              getRowId={(row) => `${row.id}-${row.userName}`}
              //             customStyles={{
              //   dataGridWrapper: {
              //     overflowX: "hidden !important",
              //     "& .MuiDataGrid-virtualScroller": {
              //       overflowX: "auto !important",
              //     },
              //     "& .MuiDataGrid-main": {
              //       overflowX: "hidden !important",
              //     },
              //   },
              // }}
            />
          </Box>
          <Box {...styles("input")} sx={{ mt: 2, fontWeight: 700 }}>
            Total Available Land Area: {landArea} hectares
          </Box>
          <Box {...styles("inputContainer")}>
            <Box {...styles("input")}>
              <Input
                customStyles={{
                  label: defaultStyles.label,
                  wrapper: defaultStyles.selectWrapper,
                }}
                name="totalArea"
                control={control}
                errors={errors}
                placeholder="Enter Area Manually for Plantation activities"
                label="Enter Area Manually for Plantation activities"
                type="number"
                rules={{
                  required: {
                    value: !watch("selectLandArea"),
                    message: errorMessage.required,
                  },
                  validate: (value) => {
                    const selected = Number(value);
                    const available = Number(landArea);
                    const remaining = available - selected;

                    // Minimum selected area
                    if (selected < 5) {
                      return "Area should not be less than 5 hectares";
                    }

                    // Cannot exceed available land
                    if (selected > available) {
                      return "Area should not be greater than available area";
                    }
                    // Remaining land rule
                    if (remaining < 5 && remaining != 0) {
                      return "Remaining area should not be less than 5 hectares";
                    }
                    return true;
                  },
                }}
              />
            </Box>
            {+landArea > 0 && (
              <Box {...styles("checkbox")}>
                <Checkbox
                  name="selectLandArea"
                  control={control}
                  label={"Select Total Available Land for Plantation"}
                  isManual
                  handleChange={(e) => {
                    totalSelectedLand();
                    // trigger();
                  }}
                />
              </Box>
            )}

            {/* {!getValues("selectLandArea") && (
              <Box {...styles("input")}>
                <Input
                  customStyles={{
                    label: defaultStyles.label,
                    wrapper: defaultStyles.selectWrapper,
                  }}
                  name="totalArea"
                  rules={{
                    required: {
                      value: !watch("selectLandArea"),
                      message: errorMessage.required,
                    },
                    min: {
                      value: 5,
                      message: "Area should be greater than 5 hectare",
                    },
                    max: {
                      value: landArea,
                      message: "Area should be greater than available Area",
                    },
                  }}
                  control={control}
                  errors={errors}
                  placeholder="Enter Area Manually for Plantation activities"
                  label="Enter Area Manually for Plantation activities"
                  type="number"
                />
                <Box
                  sx={{
                    color: "#272727",
                    fontSize: "11px",
                    fontWeight: "400",
                    position: "relative",
                    bottom: !!errors.totalArea ? "-4px" : "10px",
                  }}
                >
                  (Area should not be less than 5 hectares and remaining area
                  should not be less than 5 hectares.)
                </Box>
              </Box>
            )} */}
          </Box>

          <Box
            sx={{
              maxWidth: 800,
              p: 2,
              mt: 2,
              border: "1px solid #ddd",
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Probable Utilization of Green Credits:
            </Typography>

            <Box sx={{ pl: 2 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                i. The Green Credits generated under the Green Credit Programme
                may be exchanged once, for one of the following purposes:
              </Typography>
              <FormControl component="fieldset" fullWidth>
                <RadioGroup
                  aria-label="green-credit-utilization"
                  name="green-credit-utilization-group"
                  value={selectedValue}
                  onChange={handleChange}
                >
                  {utilizationOptions.map((option) => (
                    <FormControlLabel
                      key={option.id}
                      value={option.id}
                      control={<Radio size="small" />}
                      label={
                        <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                          <Typography
                            component="span"
                            sx={{ mt: 0.5, lineHeight: 1.5 }}
                          >
                            <Typography
                              component="span"
                              sx={{ fontWeight: "bold" }}
                            >
                              {option.id}.
                            </Typography>{" "}
                            {option.label}
                          </Typography>
                        </Box>
                      }
                      sx={{
                        alignItems: "flex-start",
                        margin: "8px 0",
                      }}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
              <Typography
                variant="caption"
                color="text.secondary"
                component="p"
                sx={{ mt: 2, fontStyle: "italic" }}
              >
                (*Once the Green Credits are exchanged for purposes mentioned
                above, it shall be deemed to be extinguished to the extent of
                Green Credit exchanged and cannot be used again.)
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              maxWidth: 800,
              pt: 2,
              borderTop: "1px solid #e0e0e0",
              mt: 2,
            }}
          >
            <Typography variant="body1" sx={{ mb: 1, fontWeight: "bold" }}>
              ii. The Green Credit generated may be used for:
            </Typography>
            <Box sx={{ pl: 2 }}>
              <FormControl component="fieldset" fullWidth>
                <RadioGroup
                  aria-label="green-credit-utilization-2"
                  name="green-credit-utilization-group-2"
                  value={selectedValue2}
                >
                  <FormControlLabel
                    value={UTILIZATION_2_VALUE}
                    control={
                      <Radio
                        size="small"
                        onClick={() =>
                          setSelectedValue2((prev) =>
                            prev === UTILIZATION_2_VALUE
                              ? null
                              : UTILIZATION_2_VALUE
                          )
                        }
                      />
                    }
                    label={
                      <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                        <Typography component="span" sx={{ lineHeight: 1.5 }}>
                          {UTILIZATION_2_CONTENT}
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: "flex-start", margin: "8px 0" }}
                  />
                </RadioGroup>
              </FormControl>
            </Box>
          </Box>
          {/*checkbox */}

          <Box {...styles("selfDeclaration")} sx={{ color: "red" }}>
            Please select a utilisation type and agree to both conditions
          </Box>
          <Box
            sx={{
              mb: "40px",
            }}
          >
            <Checkbox
              name="nonTradableCredit"
              control={control}
              label={
                "The Green Credits issued for tree plantation under GCP shall be non-tradable."
              }
              rules={{ required: true }}
              isManual
              customStyles={{ wrapper: { marginBottom: "16px" } }}
              handleChange={(e) => {
                const checked = e.target.checked;
                const labelText =
                  "The Green Credits issued for tree plantation under GCP shall be non-tradable.";
                setValue("nonTradableCredit", checked ? labelText : "");
              }}
            />
            <Checkbox
              name="nonTransferableCredit"
              control={control}
              label={
                "The Green credits issued for the tree plantation under the GCP shall be non-transferable except between the holding company and its subsidiary companies as mentioned by the GCA on the GCP portal at the time of registration of the GCA"
              }
              rules={{ required: true }}
              isManual
              handleChange={(e) => {
                const checked = e.target.checked;
                const labelText =
                  "The Green credits issued for the tree plantation under the GCP shall be non-transferable except between the holding company and its subsidiary companies as mentioned by the GCA on the GCP portal at the time of registration of the GCA";
                setValue("nonTransferableCredit", checked ? labelText : "");
              }}
            />
          </Box>

          <Box>
            <ViewImages fileUrl={kmlFileUrl} label={"KML File"} />
          </Box>
          <Box
            sx={{
              border: "2px solid",
              borderColor: "#65a765",
              backgroundColor: "#e6ffe6",
              margin: "30px 0px",
            }}
          >
            <MapComponent kmlUrl={kmlFileUrl} />
          </Box>
        </Box>
        <Box>
          <Box sx={{ mt: "50px" }}>
            <Box
              sx={{
                border: "1px solid",
                borderRadius: "0.5rem",
                borderColor: "rgba(229, 231, 235, 1)",
                backgroundColor: "#F9FAFB",
                padding: "10px",
                margin: "30px 0px",
              }}
            >
              <Typography
                variant="h6"
                component="h1"
                sx={{ mb: 1, fontWeight: 600 }}
              >
                Eco-Restoration Block Details
              </Typography>
              <Box
                sx={{
                  backgroundColor: "#FFFFFF",
                  padding: "3px",
                  margin: "5px 0px",
                }}
              >
                <Box {...styles("flexColumn")}>
                  <Box {...styles("containerGap")}>
                    <ViewForm
                      label={"Parcel ID"}
                      value={projectData?.registrationNo}
                      customStyles={{
                        wrapper: { flexDirection: "row", marginBottom: "1%" },
                        value: { fontWeight: 400, wordBreak: "normal" },
                        label: {
                          whiteSpace: "nowrap",
                          wordBreak: "keep-all",
                          flexShrink: 0,
                        },
                      }}
                    />
                    <ViewForm
                      label={"State"}
                      value={projectData?.address?.stateName}
                      customStyles={{
                        wrapper: { flexDirection: "row", marginBottom: "1%" },
                        value: { fontWeight: 400, wordBreak: "normal" },
                        label: {
                          whiteSpace: "nowrap",
                          wordBreak: "keep-all",
                          flexShrink: 0,
                        },
                      }}
                    />
                    <ViewForm
                      label={"Circle"}
                      value={projectData?.landSpecification?.circleName}
                      customStyles={{
                        wrapper: { flexDirection: "row", marginBottom: "1%" },
                        value: { fontWeight: 400, wordBreak: "normal" },
                        label: {
                          whiteSpace: "nowrap",
                          wordBreak: "keep-all",
                          flexShrink: 0,
                        },
                      }}
                    />
                    <ViewForm
                      label={"Forest Range"}
                      value={projectData?.landSpecification?.range}
                      customStyles={{
                        wrapper: { flexDirection: "row", marginBottom: "1%" },
                        value: { fontWeight: 400, wordBreak: "normal" },
                        label: {
                          whiteSpace: "nowrap",
                          wordBreak: "keep-all",
                          flexShrink: 0,
                        },
                      }}
                    />
                    <ViewForm
                      label={"Compartment No"}
                      value={projectData?.landSpecification?.compartmentNumber}
                      customStyles={{
                        wrapper: { flexDirection: "row", marginBottom: "1%" },
                        value: { fontWeight: 400, wordBreak: "normal" },
                        label: {
                          whiteSpace: "nowrap",
                          wordBreak: "keep-all",
                          flexShrink: 0,
                        },
                      }}
                    />
                    <ViewForm
                      label={"Beat"}
                      value={projectData?.landSpecification?.beat}
                      customStyles={{
                        wrapper: { flexDirection: "row", marginBottom: "1%" },
                        value: { fontWeight: 400, wordBreak: "normal" },
                        label: {
                          whiteSpace: "nowrap",
                          wordBreak: "keep-all",
                          flexShrink: 0,
                        },
                      }}
                    />
                    <ViewForm
                      label={"Total Land Area"}
                      value={
                        projectData?.landArea
                          ? `${projectData?.landArea} hectares`
                          : "-"
                      }
                      customStyles={{
                        wrapper: { flexDirection: "row", marginBottom: "1%" },
                        value: { fontWeight: 400, wordBreak: "normal" },
                        label: {
                          whiteSpace: "nowrap",
                          wordBreak: "keep-all",
                          flexShrink: 0,
                        },
                      }}
                    />
                  </Box>
                  <Box>
                    <ViewForm
                      label={"Name Of Site"}
                      value={projectData?.landSpecification?.siteName}
                      customStyles={{
                        wrapper: { flexDirection: "row", marginBottom: "1%" },
                        value: { fontWeight: 400, wordBreak: "normal" },
                        label: {
                          whiteSpace: "nowrap",
                          wordBreak: "keep-all",
                          flexShrink: 0,
                        },
                      }}
                    />
                    <ViewForm
                      label={"District"}
                      value={projectData?.address?.districtName}
                      customStyles={{
                        wrapper: { flexDirection: "row", marginBottom: "1%" },
                        value: { fontWeight: 400, wordBreak: "normal" },
                        label: {
                          whiteSpace: "nowrap",
                          wordBreak: "keep-all",
                          flexShrink: 0,
                        },
                      }}
                    />
                    <ViewForm
                      label={"Division"}
                      value={projectData?.landSpecification?.division}
                      customStyles={{
                        wrapper: { flexDirection: "row", marginBottom: "1%" },
                        value: { fontWeight: 400, wordBreak: "normal" },
                        label: {
                          whiteSpace: "nowrap",
                          wordBreak: "keep-all",
                          flexShrink: 0,
                        },
                      }}
                    />

                    <ViewForm
                      label={"Revenue Block"}
                      value={projectData?.landSpecification?.revenueblock}
                      customStyles={{
                        wrapper: { flexDirection: "row", marginBottom: "1%" },
                        value: { fontWeight: 400, wordBreak: "normal" },
                        label: {
                          whiteSpace: "nowrap",
                          wordBreak: "keep-all",
                          flexShrink: 0,
                        },
                      }}
                    />
                  </Box>
                </Box>
              </Box>
              <Box
                sx={{
                  backgroundColor: "#FFFFFF",
                  padding: "3px",
                  margin: "5px 0px",
                }}
              >
                <Typography
                  variant="h6"
                  component="h1"
                  sx={{ mb: 1, fontWeight: 600 }}
                >
                  Site Details
                </Typography>
                <Box>
                  <Box {...styles("containerGap")}>
                    <ViewForm
                      label={"Type of Degraded Land"}
                      value={projectData?.landType}
                      customStyles={{
                        wrapper: { flexDirection: "row", marginBottom: "1%" },
                        value: { fontWeight: 400, wordBreak: "normal" },
                        label: {
                          whiteSpace: "nowrap",
                          wordBreak: "keep-all",
                          flexShrink: 0,
                        },
                      }}
                    />
                    <ViewForm
                      label={"Legal Status of Land"}
                      value={projectData?.landSpecification?.legalStatusOfLand}
                      customStyles={{
                        wrapper: { flexDirection: "row", marginBottom: "1%" },
                        value: { fontWeight: 400, wordBreak: "normal" },
                        label: {
                          whiteSpace: "nowrap",
                          wordBreak: "keep-all",
                          flexShrink: 0,
                        },
                      }}
                    />
                    <ViewForm
                      label={"Geological Features"}
                      value={projectData?.landSpecification?.geologicalFeatures}
                      customStyles={{
                        wrapper: { flexDirection: "row", marginBottom: "1%" },
                        value: { fontWeight: 400, wordBreak: "normal" },
                        label: {
                          whiteSpace: "nowrap",
                          wordBreak: "keep-all",
                          flexShrink: 0,
                        },
                      }}
                    />
                    <ViewForm
                      label={"Accessibility"}
                      value={projectData?.landSpecification?.accessibility}
                      customStyles={{
                        wrapper: { flexDirection: "row", marginBottom: "1%" },
                        value: { fontWeight: 400, wordBreak: "normal" },
                        label: {
                          whiteSpace: "nowrap",
                          wordBreak: "keep-all",
                          flexShrink: 0,
                        },
                      }}
                    />
                    <ViewForm
                      label={"Tree Canopy Density"}
                      value={
                        canopyDensity !== null && canopyDensity !== undefined
                          ? Number(canopyDensity).toString()
                          : "-"
                      }
                      customStyles={{
                        wrapper: { flexDirection: "row", marginBottom: "1%" },
                        value: { fontWeight: 400, wordBreak: "normal" },
                        label: {
                          whiteSpace: "nowrap",
                          wordBreak: "keep-all",
                          flexShrink: 0,
                        },
                      }}
                    />
                    <ViewForm
                      label={"Slope And Terrain"}
                      value={projectData?.landSpecification?.slopeAndTerrain}
                      customStyles={{
                        wrapper: { flexDirection: "row", marginBottom: "1%" },
                        value: { fontWeight: 400, wordBreak: "normal" },
                        label: {
                          whiteSpace: "nowrap",
                          wordBreak: "keep-all",
                          flexShrink: 0,
                        },
                      }}
                    />
                    <ViewForm
                      label={"Slope"}
                      value={projectData?.landSpecification?.slope}
                      customStyles={{
                        wrapper: { flexDirection: "row", marginBottom: "1%" },
                        value: { fontWeight: 400, wordBreak: "normal" },
                        label: {
                          whiteSpace: "nowrap",
                          wordBreak: "keep-all",
                          flexShrink: 0,
                        },
                      }}
                    />
                    <ViewForm
                      label={"Terrain"}
                      value={projectData?.landSpecification?.terrain}
                      customStyles={{
                        wrapper: { flexDirection: "row", marginBottom: "1%" },
                        value: { fontWeight: 400, wordBreak: "normal" },
                        label: {
                          whiteSpace: "nowrap",
                          wordBreak: "keep-all",
                          flexShrink: 0,
                        },
                      }}
                    />
                  </Box>
                </Box>
              </Box>
              {/*soil */}
              <Box
                sx={{
                  backgroundColor: "#FFFFFF",
                  padding: "3px",
                  margin: "5px 0px",
                }}
              >
                <Typography
                  variant="h6"
                  component="h1"
                  sx={{ mb: 1, fontWeight: 600 }}
                >
                  Soil Characteristics
                </Typography>
                <Box>
                  <Box {...styles("containerGap")}>
                    <ViewForm
                      label={"Soil Type"}
                      value={projectData?.landSpecification?.soilType}
                      customStyles={{
                        wrapper: { flexDirection: "row", marginBottom: "1%" },
                        value: { fontWeight: 400, wordBreak: "normal" },
                        label: {
                          whiteSpace: "nowrap",
                          wordBreak: "keep-all",
                          flexShrink: 0,
                        },
                      }}
                    />
                    <ViewForm
                      label={" Soil Depth & Texture"}
                      value={
                        projectData?.landSpecification?.soilDepthAndTexture
                      }
                      customStyles={{
                        wrapper: { flexDirection: "row", marginBottom: "1%" },
                        value: { fontWeight: 400, wordBreak: "normal" },
                        label: {
                          whiteSpace: "nowrap",
                          wordBreak: "keep-all",
                          flexShrink: 0,
                        },
                      }}
                    />
                    <ViewForm
                      label={"Soil pH / fertility"}
                      value={projectData?.landSpecification?.soilpH}
                      customStyles={{
                        wrapper: { flexDirection: "row", marginBottom: "1%" },
                        value: { fontWeight: 400, wordBreak: "normal" },
                        label: {
                          whiteSpace: "nowrap",
                          wordBreak: "keep-all",
                          flexShrink: 0,
                        },
                      }}
                    />
                    <ViewForm
                      label={"Soil Moisture Regime"}
                      value={projectData?.landSpecification?.soilMoistureRegime}
                      customStyles={{
                        wrapper: { flexDirection: "row", marginBottom: "1%" },
                        value: { fontWeight: 400, wordBreak: "normal" },
                        label: {
                          whiteSpace: "nowrap",
                          wordBreak: "keep-all",
                          flexShrink: 0,
                        },
                      }}
                    />
                    <ViewForm
                      label={"Edaphic Details"}
                      value={projectData?.landSpecification?.edaphicDetails}
                      customStyles={{
                        wrapper: { flexDirection: "row", marginBottom: "1%" },
                        value: { fontWeight: 400, wordBreak: "normal" },
                        label: {
                          whiteSpace: "nowrap",
                          wordBreak: "keep-all",
                          flexShrink: 0,
                        },
                      }}
                    />
                  </Box>
                </Box>
              </Box>
              {/*water*/}
              <Box
                sx={{
                  backgroundColor: "#FFFFFF",
                  padding: "3px",
                  margin: "5px 0px",
                }}
              >
                <Typography
                  variant="h6"
                  component="h1"
                  sx={{ mb: 1, fontWeight: 600 }}
                >
                  Water Availability
                </Typography>
                <Box>
                  <Box {...styles("containerGap")}>
                    <ViewForm
                      label={"Source of Water"}
                      value={projectData?.landSpecification?.sourceOfWater}
                      customStyles={{
                        wrapper: { flexDirection: "row", marginBottom: "1%" },
                        value: { fontWeight: 400, wordBreak: "normal" },
                        label: {
                          whiteSpace: "nowrap",
                          wordBreak: "keep-all",
                          flexShrink: 0,
                        },
                      }}
                    />
                    <ViewForm
                      label={"Water Availability"}
                      value={projectData?.landSpecification?.availability}
                      customStyles={{
                        wrapper: { flexDirection: "row", marginBottom: "1%" },
                        value: { fontWeight: 400, wordBreak: "normal" },
                        label: {
                          whiteSpace: "nowrap",
                          wordBreak: "keep-all",
                          flexShrink: 0,
                        },
                      }}
                    />
                    <ViewForm
                      label={"Distance to Water Source"}
                      value={
                        projectData?.landSpecification
                          ?.distanceToNearestWaterSource
                      }
                      customStyles={{
                        wrapper: { flexDirection: "row", marginBottom: "1%" },
                        value: { fontWeight: 400, wordBreak: "normal" },
                        label: {
                          whiteSpace: "nowrap",
                          wordBreak: "keep-all",
                          flexShrink: 0,
                        },
                      }}
                    />
                    <ViewForm
                      label={"Average rainfall"}
                      value={projectData?.landSpecification?.averagRainfall}
                      customStyles={{
                        wrapper: { flexDirection: "row", marginBottom: "1%" },
                        value: { fontWeight: 400, wordBreak: "normal" },
                        label: {
                          whiteSpace: "nowrap",
                          wordBreak: "keep-all",
                          flexShrink: 0,
                        },
                      }}
                    />
                    <ViewForm
                      label={"Hydrological Conditions"}
                      value={
                        projectData?.landSpecification?.hydrologicalConditions
                      }
                      customStyles={{
                        wrapper: { flexDirection: "row", marginBottom: "1%" },
                        value: { fontWeight: 400, wordBreak: "normal" },
                        label: {
                          whiteSpace: "nowrap",
                          wordBreak: "keep-all",
                          flexShrink: 0,
                        },
                      }}
                    />
                  </Box>
                </Box>
              </Box>
              {/*flora*/}

              <Box
                sx={{
                  backgroundColor: "#FFFFFF",
                  padding: "3px",
                  margin: "5px 0px",
                }}
              >
                <Typography
                  variant="h6"
                  component="h1"
                  sx={{ mb: 1, fontWeight: 600 }}
                >
                  Flora & Fauna
                </Typography>
                <Box>
                  <Box {...styles("containerGap")}>
                    <ViewForm
                      label={"Forest Type"}
                      value={projectData?.landSpecification?.forestType}
                      customStyles={{
                        wrapper: { flexDirection: "row", marginBottom: "1%" },
                        value: { fontWeight: 400, wordBreak: "normal" },
                        label: {
                          whiteSpace: "nowrap",
                          wordBreak: "keep-all",
                          flexShrink: 0,
                        },
                      }}
                    />
                    <ViewForm
                      label={"Invasive Species"}
                      value={
                        projectData?.landSpecification?.invasiveSpeciesDetails
                      }
                      customStyles={{
                        wrapper: { flexDirection: "row", marginBottom: "1%" },
                        value: { fontWeight: 400, wordBreak: "normal" },
                        label: {
                          whiteSpace: "nowrap",
                          wordBreak: "keep-all",
                          flexShrink: 0,
                        },
                      }}
                    />
                  </Box>
                  <Box>
                    <ViewForm
                      label={
                        "Existing Fauna (Native Animals, wildlife, birds etc)"
                      }
                      value={projectData?.landSpecification?.existingFauna}
                      customStyles={{
                        wrapper: { flexDirection: "row", marginBottom: "1%" },
                        value: { fontWeight: 400, wordBreak: "normal" },
                        label: {
                          whiteSpace: "nowrap",
                          wordBreak: "keep-all",
                          flexShrink: 0,
                        },
                      }}
                    />
                    <ViewForm
                      label={"Type of Vegetation"}
                      value={projectData?.landSpecification?.typeOfVegetation}
                      customStyles={{
                        wrapper: { flexDirection: "row", marginBottom: "1%" },
                        value: { fontWeight: 400, wordBreak: "normal" },
                        label: {
                          whiteSpace: "nowrap",
                          wordBreak: "keep-all",
                          flexShrink: 0,
                        },
                      }}
                    />
                  </Box>
                </Box>
              </Box>
              {/* Socio-Environmental Context */}

              <Box
                sx={{
                  backgroundColor: "#FFFFFF",
                  padding: "3px",
                  margin: "5px 0px",
                }}
              >
                <Typography
                  variant="h6"
                  component="h1"
                  sx={{ mb: 1, fontWeight: 600 }}
                >
                  Socio-Environmental Context
                </Typography>
                <Box>
                  <Box {...styles("containerGap")}>
                    <ViewForm
                      label={"Nearest village"}
                      value={projectData?.landSpecification?.nearestVillage}
                      customStyles={{
                        wrapper: { flexDirection: "row", marginBottom: "1%" },
                        value: { fontWeight: 400, wordBreak: "normal" },
                        label: {
                          whiteSpace: "nowrap",
                          wordBreak: "keep-all",
                          flexShrink: 0,
                        },
                      }}
                    />
                    <ViewForm
                      label={"Rights and Usage"}
                      value={projectData?.landSpecification?.rightsAndUsage}
                      customStyles={{
                        wrapper: { flexDirection: "row", marginBottom: "1%" },
                        value: { fontWeight: 400, wordBreak: "normal" },
                        label: {
                          whiteSpace: "nowrap",
                          wordBreak: "keep-all",
                          flexShrink: 0,
                        },
                      }}
                    />
                    <ViewForm
                      label={"Community Dependency"}
                      value={
                        projectData?.landSpecification?.communityDependency
                      }
                      customStyles={{
                        wrapper: { flexDirection: "row", marginBottom: "1%" },
                        value: { fontWeight: 400, wordBreak: "normal" },
                        label: {
                          whiteSpace: "nowrap",
                          wordBreak: "keep-all",
                          flexShrink: 0,
                        },
                      }}
                    />
                  </Box>
                  <Box>
                    <ViewForm
                      label={"Existing Restrictions"}
                      value={
                        projectData?.landSpecification?.anyExistingRestrictions
                      }
                      customStyles={{
                        wrapper: { flexDirection: "row", marginBottom: "1%" },
                        value: { fontWeight: 400, wordBreak: "normal" },
                        label: {
                          whiteSpace: "nowrap",
                          wordBreak: "keep-all",
                          flexShrink: 0,
                        },
                      }}
                    />
                    <ViewForm
                      label={"Distance to habitations"}
                      value={
                        projectData?.landSpecification
                          ?.proximityToLocalCommunities
                      }
                      customStyles={{
                        wrapper: { flexDirection: "row", marginBottom: "1%" },
                        value: { fontWeight: 400, wordBreak: "normal" },
                        label: {
                          whiteSpace: "nowrap",
                          wordBreak: "keep-all",
                          flexShrink: 0,
                        },
                      }}
                    />
                  </Box>
                </Box>
              </Box>
              <Box
                sx={{
                  backgroundColor: "#FFFFFF",
                  padding: "3px",
                  margin: "5px 0px",
                }}
              >
                <Typography
                  variant="h6"
                  component="h1"
                  sx={{ mb: 1, fontWeight: 600 }}
                >
                  Forest Department Undertaking
                </Typography>
                <Typography variant="subtitle1" component="h1" sx={{ mb: 1 }}>
                  The land is free from all types of encumbrances and is under
                  the management of the Union Territory/State Forest Department
                  and suitable for eco-restoration under GCP
                </Typography>
              </Box>
            </Box>
          </Box>

          {/*images*/}
          <Box {...styles("formWrapper")}>
            <Box {...styles("heading")}>GeoTagged Images</Box>
            <Box {...styles("imageWrapper2")}>
              {projectImages?.length ? (
                <>
                  {projectImages.map((item) => (
                    <Card
                      key={item?.imageUrl?.fileUrl}
                      title={`GeoTagged Image`}
                      image={item?.imageUrl?.fileUrl}
                    />
                  ))}
                </>
              ) : (
                <></>
              )}
            </Box>
          </Box>
          {/*video*/}
          <Box {...styles("videoFormWrapper")}>
            <Box {...styles("heading")}>Drone Video</Box>
            {!droneVideoUrl ? (
              "No Drone Video Available"
            ) : (
              <video
                src={droneVideoUrl}
                controls
                width="100%"
                style={{ borderRadius: "8px" }}
              />
            )}
          </Box>
          <Box mt={3} textAlign="right">
            <Button
              variant="contained"
              disabled={(!isSubmitBlocked && !isValid) || submitLoading}
              onClick={handleSubmitForm}
            >
              Submit
            </Button>
          </Box>
        </Box>
      </Box>
      <SuccessModal
        open={openModal.open}
        heading={openModal.heading}
        subheading={openModal.subheading}
        description={openModal.description}
        btnText={openModal.btnText}
        type={openModal.type}
        handleModalClose={() =>
          setOpenModal((prev) => ({ ...prev, open: false }))
        }
        onClick={() => {
          setOpenModal((prev) => ({ ...prev, open: false }));
          navigate("/selected");
        }}
        closeOnBackDrop={false}
      />
      {/* Eco-Restoration Block Blocked Dialog */}
      <Dialog
        open={showBlockedDialog}
        onClose={() => setShowBlockedDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: "300px",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: "1.5rem" }}>
          Eco-Restoration Blocks Unavailable
        </DialogTitle>
        <DialogContent
          sx={{
            pt: 3,
            minHeight: "200px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" sx={{ textAlign: "center", width: "100%" }}>
            Selection of Eco-Restoration Blocks shall be made available from{" "}
            <strong>03:00 PM</strong> onwards on{" "}
            <strong>08.06.2026 (Monday)</strong>.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="contained"
            onClick={() => setShowBlockedDialog(false)}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmDialog}
        onClose={handleCancelSubmit}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Confirm Eco-Restoration Block Selection
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="textSecondary" fontWeight={600}>
            Are you sure you want to submit the eco-restoration block selection?
            This action will register the selected land area for tree plantation
            under the Green Credit Programme (GCP).
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button variant="outlined" onClick={handleCancelSubmit}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmSubmit}
            disabled={submitLoading}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LandParcelSelection;
